// src/components/avatar/avatarWear.js
//
// What an avatar WEARS, drawn in the same 100 x 100 box as avatarParts.js:
//   Top      — T-shirt, hoodie, zip hoodie, sweater, polo, jersey, jacket,
//              varsity jacket, in any clothes colour (or rainbow, tie-dye,
//              gold, galaxy)
//   Eyewear  — 20 glasses + 20 sunglasses (avatarCatalog.json), drawn on
//              the eyes so they always fit
//   Pin      — a little enamel pin on the chest
//
// Also used by the avatar maker to draw small previews of each item.

import React from "react";
import { CATALOG_BY_ID } from "./avatarCatalog";

const INK = "#2e2430";

const shade = (hex, f) => {
  const n = parseInt(String(hex).slice(1), 16);
  const ch = (v) => Math.max(0, Math.min(255, Math.round(v)));
  const m = f > 1 ? (v) => v + (255 - v) * (f - 1) : (v) => v * f;
  return `#${[n >> 16, (n >> 8) & 255, n & 255].map((v) => ch(m(v)).toString(16).padStart(2, "0")).join("")}`;
};

// album-reward colours (step 5) — can't be bought, only won
export const EXCLUSIVE_GRADIENTS = {
  nebula: ["#ff6fd8", "#8b5cf6", "#3a2a9e", "#1d6fe0"],
  jungle: ["#a8e063", "#2f8a3e", "#1e5c2a", "#7fbf3f"],
  ocean: ["#7fe7ff", "#1f8fd6", "#1b4fa8", "#45c6e8"],
  neon: ["#ff3fa4", "#ffe14d", "#3fffd2", "#a24dff"],
  glow: ["#d4ff6b", "#3fe0a0", "#1fb5ad", "#9cff9a"],
  blossom: ["#ffd1e8", "#ff8cc6", "#ff5fa2", "#ffe0f0"],
};

const RAINBOW = ["#ff4d6d", "#ff9a3c", "#ffd23f", "#4cd964", "#3a86ff", "#8b5cf6"];

// ---------------------------------------------------------------------------
// paints: plain colours, or a gradient/pattern for the special colours
// ---------------------------------------------------------------------------
export const paintDefs = (rid, item) => {
  const id = `${rid}paint`;
  switch (item?.pattern) {
    case "rainbow":
      return (
        <linearGradient id={id} x1="0" y1="0" x2="1" y2="0">
          {RAINBOW.map((c, i) => (
            <stop key={i} offset={i / (RAINBOW.length - 1)} stopColor={c} />
          ))}
        </linearGradient>
      );
    case "tiedye":
      return (
        <radialGradient id={id} cx="0.5" cy="0.35" r="0.8">
          {["#ff6fb5", "#ffd23f", "#4cd9b0", "#5aa2ff", "#b07cff", "#ff6fb5"].map((c, i) => (
            <stop key={i} offset={i / 5} stopColor={c} />
          ))}
        </radialGradient>
      );
    case "gold":
      return (
        <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#fff1a8" />
          <stop offset="0.35" stopColor="#f2c230" />
          <stop offset="0.6" stopColor="#c8901a" />
          <stop offset="1" stopColor="#f7d560" />
        </linearGradient>
      );
    case "galaxy":
      return (
        <radialGradient id={id} cx="0.4" cy="0.3" r="0.9">
          <stop offset="0" stopColor="#7b5cff" />
          <stop offset="0.5" stopColor="#3b2a86" />
          <stop offset="1" stopColor="#140f36" />
        </radialGradient>
      );
    default: {
      const g = EXCLUSIVE_GRADIENTS[item?.pattern];
      if (!g) return null;
      return (
        <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
          {g.map((c, i) => (
            <stop key={i} offset={i / (g.length - 1)} stopColor={c} />
          ))}
        </linearGradient>
      );
    }
  }
};

const paintOf = (rid, item) => (item?.pattern ? `url(#${rid}paint)` : item?.fill || "#2f6fd6");
const baseOf = (item) => item?.fill || "#2f6fd6";

// ---------------------------------------------------------------------------
// TOPS
// ---------------------------------------------------------------------------
const BODY = "M8 104 C9 86 21 77.5 36 74.5 L64 74.5 C79 77.5 91 86 92 104 Z";

export const Top = ({ id, paint, rid }) => {
  const fill = paintOf(rid, paint);
  const base = baseOf(paint);
  const ink = shade(base, 0.55);
  const light = shade(base, 1.25);
  const dark = shade(base, 0.8);
  const kind = String(id || "av-top-tee").replace("av-top-", "");
  const body = <path d={BODY} fill={fill} stroke={ink} strokeWidth="1.2" strokeLinejoin="round" />;
  const sleeves = (
    <path d="M22 88 C24 94 25 99 25 104 M78 88 C76 94 75 99 75 104" stroke={ink} strokeWidth="0.9" fill="none" opacity="0.6" />
  );
  const stars =
    paint?.pattern === "galaxy" ? (
      <g fill="#fff">
        {[[20, 92], [30, 84], [40, 98], [62, 90], [72, 82], [82, 96], [52, 86], [28, 100], [68, 100]].map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={i % 3 ? 0.6 : 1} opacity="0.85" />
        ))}
      </g>
    ) : null;
  const tee = (x0, x1, y0 = 74) => (
    <path d={`M${x0} ${y0} Q50 ${y0 + 9} ${x1} ${y0} L${x1 + 1} 104 L${x0 - 1} 104 Z`} fill="#fbfbfb" stroke="#c9ccd3" strokeWidth="0.9" />
  );

  switch (kind) {
    case "hoodie":
      return (
        <g>
          {body}
          {stars}
          {sleeves}
          {/* the hood, lying around the neck */}
          <path d="M30 79 C29 71 36 66.5 44.6 67.5 L45 76 C40 76.5 34 77.5 30 79 Z" fill={dark} stroke={ink} strokeWidth="1.1" strokeLinejoin="round" />
          <path d="M70 79 C71 71 64 66.5 55.4 67.5 L55 76 C60 76.5 66 77.5 70 79 Z" fill={dark} stroke={ink} strokeWidth="1.1" strokeLinejoin="round" />
          <path d="M33 76 C35 72 39 70 43 70 M67 76 C65 72 61 70 57 70" stroke={shade(base, 0.68)} strokeWidth="0.8" fill="none" />
          <path d="M45 76 Q50 80 55 76" stroke={ink} strokeWidth="1" fill="none" />
          <path d="M46 78 L45 92 M54 78 L55 92" stroke="#fff" strokeWidth="1.3" strokeLinecap="round" />
          <rect x="44" y="91.5" width="2" height="3" rx="0.8" fill="#d7dbe2" />
          <rect x="54" y="91.5" width="2" height="3" rx="0.8" fill="#d7dbe2" />
        </g>
      );
    case "zip":
      return (
        <g>
          {body}
          {stars}
          {sleeves}
          {tee(43, 57, 75)}
          <path d="M43 76 L42 104 M57 76 L58 104" stroke={ink} strokeWidth="1.1" />
          <path d="M42.5 80 L42 104 M57.5 80 L58 104" stroke="#c7cbd3" strokeWidth="1" strokeDasharray="1 1" />
          <path d="M31 77 C33 69 67 69 69 77 C66 80 60 78 57 76 L43 76 C40 78 34 80 31 77 Z" fill={dark} stroke={ink} strokeWidth="1.1" />
        </g>
      );
    case "sweater":
      return (
        <g>
          {body}
          {stars}
          {sleeves}
          <path d="M39 75 Q50 85 61 75 L58.5 74.5 Q50 81 41.5 74.5 Z" fill={dark} stroke={ink} strokeWidth="1" />
          <path d="M43 76.5 l0.5 2 M47 78 l0 2.2 M53 78 l0 2.2 M57 76.5 l-0.5 2" stroke={ink} strokeWidth="0.6" />
          <path d="M44 88 l3 4 -3 4 M56 88 l-3 4 3 4 M50 86 l0 14" stroke={light} strokeWidth="1.1" fill="none" opacity="0.8" />
        </g>
      );
    case "polo":
      return (
        <g>
          {body}
          {stars}
          {sleeves}
          <path d="M40 74 L50 82 L60 74 L64 79 L54 84 L50 81 L46 84 L36 79 Z" fill={light} stroke={ink} strokeWidth="1" strokeLinejoin="round" />
          <path d="M50 82 L50 94" stroke={ink} strokeWidth="0.9" />
          <circle cx="51.5" cy="86" r="0.9" fill="#fff" stroke={ink} strokeWidth="0.4" />
          <circle cx="51.5" cy="90.5" r="0.9" fill="#fff" stroke={ink} strokeWidth="0.4" />
        </g>
      );
    case "jersey":
      return (
        <g>
          {body}
          {stars}
          <path d="M40 74.5 L50 86 L60 74.5" fill="#fff" stroke={ink} strokeWidth="1" />
          <path d="M42.5 74.5 L50 83 L57.5 74.5" fill={fill} stroke="none" />
          <path d="M21 84 L26 104 M79 84 L74 104" stroke="#fff" strokeWidth="2.4" />
          <text x="50" y="100" textAnchor="middle" fontSize="13" fontWeight="900" fontFamily="Arial Rounded MT Bold, Arial, sans-serif" fill="#fff" stroke={ink} strokeWidth="0.6">
            1
          </text>
        </g>
      );
    case "jacket":
      return (
        <g>
          {body}
          {stars}
          {sleeves}
          {tee(42, 58, 75)}
          <path d="M42 75 L38 80 L42 86 L43 104 M58 75 L62 80 L58 86 L57 104" fill="none" stroke={ink} strokeWidth="1.1" strokeLinejoin="round" />
          <path d="M36 75.5 L42 75 L38 80 L42 86 L35 80 Z" fill={dark} stroke={ink} strokeWidth="1" strokeLinejoin="round" />
          <path d="M64 75.5 L58 75 L62 80 L58 86 L65 80 Z" fill={dark} stroke={ink} strokeWidth="1" strokeLinejoin="round" />
          <circle cx="40" cy="94" r="1" fill={light} stroke={ink} strokeWidth="0.5" />
          <circle cx="60" cy="94" r="1" fill={light} stroke={ink} strokeWidth="0.5" />
          <rect x="62" y="88" width="9" height="6" rx="1" fill="none" stroke={ink} strokeWidth="0.8" opacity="0.7" />
        </g>
      );
    case "varsity":
      return (
        <g>
          {body}
          <path d="M8 104 C9 86 21 77.5 30 75.5 C27 85 26 95 26 104 Z M92 104 C91 86 79 77.5 70 75.5 C73 85 74 95 74 104 Z" fill="#f4f1e8" stroke={ink} strokeWidth="1.1" />
          {stars}
          <path d="M37 74.5 Q50 86 63 74.5" fill="none" stroke="#f4f1e8" strokeWidth="3.2" />
          <path d="M37 74.5 Q50 86 63 74.5" fill="none" stroke={ink} strokeWidth="0.8" strokeDasharray="0" opacity="0.6" />
          <path d="M50 82 L50 104" stroke={ink} strokeWidth="1" />
          {[86, 91, 96, 101].map((y) => (
            <circle key={y} cx="52" cy={y} r="0.9" fill="#f4f1e8" stroke={ink} strokeWidth="0.4" />
          ))}
          <text x="38" y="96" textAnchor="middle" fontSize="10" fontWeight="900" fontFamily="Georgia, serif" fill="#f4f1e8" stroke={ink} strokeWidth="0.6">
            S
          </text>
        </g>
      );
    default:
      // T-shirt
      return (
        <g>
          {body}
          {stars}
          {sleeves}
          <path d="M40 74.5 Q50 84 60 74.5" fill="none" stroke={dark} strokeWidth="2.4" />
          <path d="M40 74.5 Q50 84 60 74.5" fill="none" stroke={ink} strokeWidth="0.8" />
        </g>
      );
  }
};

// ---------------------------------------------------------------------------
// EYEWEAR — lens shapes centred on each eye (40.5,46.5) and (59.5,46.5)
// ---------------------------------------------------------------------------
const L = 40.5;
const R = 59.5;
const Y = 46.5;

const starPath = (cx, cy, r) => {
  let s = "";
  for (let i = 0; i < 10; i += 1) {
    const a = (Math.PI / 5) * i - Math.PI / 2;
    const rr = i % 2 ? r * 0.48 : r;
    s += `${i ? "L" : "M"}${(cx + Math.cos(a) * rr).toFixed(2)} ${(cy + Math.sin(a) * rr).toFixed(2)} `;
  }
  return `${s}Z`;
};

/** lens path for one eye; m = -1 left lens, +1 right lens */
const lensPath = (shape, cx, m) => {
  switch (shape) {
    case "round":
      return `M${cx - 6} ${Y} a6 6 0 1 0 12 0 a6 6 0 1 0 -12 0 Z`;
    case "square":
      return `M${cx - 7.4} ${Y - 5.6} H${cx + 7.4} Q${cx + 8} ${Y - 5.6} ${cx + 7.8} ${Y - 4.6} L${cx + 7} ${Y + 4.4} Q${cx + 6.8} ${Y + 5.6} ${cx + 5.6} ${Y + 5.6} H${cx - 5.6} Q${cx - 6.8} ${Y + 5.6} ${cx - 7} ${Y + 4.4} L${cx - 7.8} ${Y - 4.6} Q${cx - 8} ${Y - 5.6} ${cx - 7.4} ${Y - 5.6} Z`;
    case "cat": {
      const o = cx + m * 7.6; // outer corner
      const i = cx - m * 6.4;
      return `M${i} ${Y - 3.6} Q${cx} ${Y - 5.2} ${o} ${Y - 6.6} Q${o - m * 0.6} ${Y + 2.6} ${cx + m * 3} ${Y + 5} Q${cx - m * 3} ${Y + 6.2} ${i} ${Y + 2} Z`;
    }
    case "aviator":
      return `M${cx - 6.6} ${Y - 4.4} H${cx + 6.6} Q${cx + 7.6} ${Y - 4.4} ${cx + 7} ${Y - 1} Q${cx + 5.6} ${Y + 7.4} ${cx} ${Y + 6.6} Q${cx - 5.6} ${Y + 7.4} ${cx - 7} ${Y - 1} Q${cx - 7.6} ${Y - 4.4} ${cx - 6.6} ${Y - 4.4} Z`;
    case "sport":
      return `M${cx - m * 6.2} ${Y - 4.2} Q${cx} ${Y - 6} ${cx + m * 8.2} ${Y - 4.8} Q${cx + m * 8.6} ${Y + 1.4} ${cx + m * 5.6} ${Y + 4.6} Q${cx} ${Y + 6.6} ${cx - m * 5.8} ${Y + 3.4} Z`;
    case "bolt":
      return m < 0
        ? `M${cx - 7.6} ${Y - 5} L${cx + 4.6} ${Y - 5} L${cx + 1.6} ${Y - 0.6} L${cx + 6.6} ${Y - 0.6} L${cx - 2} ${Y + 6.8} L${cx - 0.4} ${Y + 1.6} L${cx - 6} ${Y + 1.6} Z`
        : `M${cx + 7.6} ${Y - 5} L${cx - 4.6} ${Y - 5} L${cx - 1.6} ${Y - 0.6} L${cx - 6.6} ${Y - 0.6} L${cx + 2} ${Y + 6.8} L${cx + 0.4} ${Y + 1.6} L${cx + 6} ${Y + 1.6} Z`;
    case "star":
      return starPath(cx, Y + 0.4, 8.2);
    case "club":
    case "rect":
    default:
      return `M${cx - 6.8} ${Y - 5} H${cx + 6.8} Q${cx + 7.8} ${Y - 5} ${cx + 7.6} ${Y - 3.6} L${cx + 7} ${Y + 3} Q${cx + 6.6} ${Y + 5.2} ${cx + 4.4} ${Y + 5.2} H${cx - 4.4} Q${cx - 6.6} ${Y + 5.2} ${cx - 7} ${Y + 3} L${cx - 7.6} ${Y - 3.6} Q${cx - 7.8} ${Y - 5} ${cx - 6.8} ${Y - 5} Z`;
  }
};

const lensPaint = (rid, lens) => {
  const id = `${rid}lens`;
  const stops = {
    clear: [["#ffffff", 0.18], ["#dff0ff", 0.28]],
    dark: [["#4b4b5c", 0.95], ["#14141c", 0.95]],
    brown: [["#8a5a2e", 0.92], ["#3b2210", 0.95]],
    green: [["#4f6b43", 0.92], ["#1d2b18", 0.95]],
    mirror: [["#7fe0ff", 1], ["#5a5cff", 1], ["#d36bff", 1]],
    rainbowlens: RAINBOW.map((c) => [c, 0.95]),
  }[lens] || [["#4b4b5c", 0.95], ["#14141c", 0.95]];
  return {
    def: (
      <linearGradient id={id} x1="0" y1="0" x2={lens === "rainbowlens" ? 1 : 0} y2={lens === "rainbowlens" ? 0 : 1}>
        {stops.map(([c, o], i) => (
          <stop key={i} offset={i / Math.max(1, stops.length - 1)} stopColor={c} stopOpacity={o} />
        ))}
      </linearGradient>
    ),
    fill: `url(#${id})`,
  };
};

const SPARKLE = (x, y, r = 1.6, c = "#fff") => (
  <path key={`${x}-${y}`} d={`M${x} ${y - r} L${x + r * 0.3} ${y - r * 0.3} L${x + r} ${y} L${x + r * 0.3} ${y + r * 0.3} L${x} ${y + r} L${x - r * 0.3} ${y + r * 0.3} L${x - r} ${y} L${x - r * 0.3} ${y - r * 0.3} Z`} fill={c} />
);

/** decorations along the top of both lenses */
const Deco = ({ extra }) => {
  const top = (k) => [L - 6 + k * 3, R - 6 + k * 3].flatMap((x) => [x]);
  const tops = [0, 1, 2, 3, 4].flatMap((k) => top(k)).sort((a, b) => a - b);
  switch (extra) {
    case "glittertop":
      return (
        <g fill="#fff">
          {tops.map((x, i) => (
            <circle key={i} cx={x} cy={Y - 5.1} r="0.75" />
          ))}
        </g>
      );
    case "gems":
      return (
        <g>
          {[[L - 7, Y - 5], [R + 7, Y - 5], [L - 3, Y - 5.2], [R + 3, Y - 5.2]].map(([x, y], i) => (
            <path key={i} d={`M${x} ${y - 1.8} l1.6 1.8 -1.6 1.8 -1.6 -1.8 Z`} fill={["#4fd1ff", "#ff6fb5", "#7dff9a", "#ffd23f"][i]} stroke="#fff" strokeWidth="0.4" />
          ))}
        </g>
      );
    case "bling":
    case "glitter":
    case "crystals":
    case "diamonds":
    case "rainbowgems":
    case "ultimate": {
      const col =
        extra === "crystals" ? ["#fff", "#ffc2e0"] : extra === "diamonds" ? ["#fff", "#dfe9ff"] : extra === "rainbowgems" ? RAINBOW : extra === "glitter" ? ["#fff6b8", "#fff"] : ["#fff", "#fff6c8"];
      const pts = [];
      [L, R].forEach((cx) => {
        for (let a = 0; a < 16; a += 1) {
          const t = (a / 16) * Math.PI * 2;
          pts.push([cx + Math.cos(t) * 7.4, Y + Math.sin(t) * 5.6]);
        }
      });
      return (
        <g>
          {pts.map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r={extra === "ultimate" ? 1.05 : 0.8} fill={col[i % col.length]} stroke={extra === "rainbowgems" ? "#fff" : "none"} strokeWidth="0.3" />
          ))}
          {(extra === "ultimate" || extra === "glitter" || extra === "diamonds") && (
            <g>
              {SPARKLE(L - 9, Y - 7, 2.2)}
              {SPARKLE(R + 9, Y - 7, 2.2)}
              {SPARKLE(R + 6, Y + 7, 1.4, "#fff6b8")}
            </g>
          )}
        </g>
      );
    }
    case "hearts":
      return (
        <g>
          {[L - 7.5, R + 7.5].map((x) => (
            <path key={x} d={`M${x} ${Y - 4} c-1.8 -1.6 -3.6 -3 -3.6 -4.6 c0 -1.8 2.4 -2.4 3.6 -0.8 c1.2 -1.6 3.6 -1 3.6 0.8 c0 1.6 -1.8 3 -3.6 4.6 Z`} fill="#ff5fa2" stroke="#fff" strokeWidth="0.6" />
          ))}
        </g>
      );
    default:
      return null;
  }
};

export const Eyewear = ({ id, rid = "ew" }) => {
  const it = CATALOG_BY_ID[id];
  if (!it || (it.kind !== "glasses" && it.kind !== "sunglasses")) return null;
  const frame = it.frame || "#26262c";
  const ink = shade(frame, 0.5);
  const lens = lensPaint(rid, it.lens || "clear");
  const frameGrad = `${rid}frame`;
  const isRainbowFrame = it.extra === "rainbow";
  const stroke = isRainbowFrame ? `url(#${frameGrad})` : frame;
  const w = it.extra === "thin" ? 1.2 : it.shape === "club" ? 1.4 : it.kind === "sunglasses" ? 2.2 : 2;

  if (it.shape === "visor") {
    const d = `M${L - 9} ${Y - 5} Q50 ${Y - 8} ${R + 9} ${Y - 5} Q${R + 10} ${Y + 1} ${R + 6} ${Y + 5} Q50 ${Y + 4} ${L - 6} ${Y + 5} Q${L - 10} ${Y + 1} ${L - 9} ${Y - 5} Z`;
    return (
      <g>
        <defs>{lens.def}</defs>
        <path d={`M${L - 9} ${Y - 3} L27.5 ${Y - 2} M${R + 9} ${Y - 3} L72.5 ${Y - 2}`} stroke={frame} strokeWidth="1.8" />
        <path d={d} fill={lens.fill} stroke={frame} strokeWidth="2" strokeLinejoin="round" />
        <path d={`M${L - 5} ${Y - 3} Q50 ${Y - 5.6} ${R + 3} ${Y - 3.4}`} stroke="#fff" strokeWidth="1.1" opacity="0.6" fill="none" strokeLinecap="round" />
      </g>
    );
  }

  const lp = lensPath(it.shape, L, -1);
  const rp = lensPath(it.shape, R, 1);
  return (
    <g>
      <defs>
        {lens.def}
        {isRainbowFrame && (
          <linearGradient id={frameGrad} x1="0" y1="0" x2="1" y2="0">
            {RAINBOW.map((c, i) => (
              <stop key={i} offset={i / 5} stopColor={c} />
            ))}
          </linearGradient>
        )}
      </defs>
      {/* arms to the ears */}
      <path d={`M${L - 7.4} ${Y - 3} L27.6 ${Y - 2.2} M${R + 7.4} ${Y - 3} L72.4 ${Y - 2.2}`} stroke={isRainbowFrame ? RAINBOW[0] : frame} strokeWidth="1.6" strokeLinecap="round" />
      <path d={lp} fill={lens.fill} />
      <path d={rp} fill={lens.fill} />
      {/* shine */}
      <path d={`M${L - 4} ${Y - 2.6} l3 -1.4 M${R - 4} ${Y - 2.6} l3 -1.4`} stroke="#fff" strokeWidth="1.1" strokeLinecap="round" opacity={it.lens === "clear" ? 0.9 : 0.55} />
      <path d={lp} fill="none" stroke={stroke} strokeWidth={w} strokeLinejoin="round" />
      <path d={rp} fill="none" stroke={stroke} strokeWidth={w} strokeLinejoin="round" />
      {it.extra === "tortoise" && (
        <g>
          <path d={lp} fill="none" stroke="#d98b3a" strokeWidth="1" strokeDasharray="1.2 2.2" />
          <path d={rp} fill="none" stroke="#d98b3a" strokeWidth="1" strokeDasharray="1.2 2.2" />
        </g>
      )}
      {(it.shape === "club" || it.extra === "twotone") && (
        <path
          d={`M${L - 7.8} ${Y - 2.4} Q${L - 7.6} ${Y - 5.4} ${L - 4} ${Y - 5.4} H${L + 6.6} Q${L + 7.8} ${Y - 5.2} ${L + 7.6} ${Y - 2.6} M${R - 7.6} ${Y - 2.6} Q${R - 7.8} ${Y - 5.2} ${R - 6.6} ${Y - 5.4} H${R + 4} Q${R + 7.6} ${Y - 5.4} ${R + 7.8} ${Y - 2.4}`}
          fill="none"
          stroke={it.shape === "club" ? "#1d1d22" : "#1d1d22"}
          strokeWidth="2.8"
          strokeLinecap="round"
        />
      )}
      {it.shape === "club" && (
        <g>
          <path d={lp} fill="none" stroke="#d6a73a" strokeWidth="0.8" />
          <path d={rp} fill="none" stroke="#d6a73a" strokeWidth="0.8" />
        </g>
      )}
      {it.extra === "twotone" && (
        <g>
          <path d={lp} fill="none" stroke="#22356b" strokeWidth="2" strokeDasharray="0 18 40" />
        </g>
      )}
      {/* bridge */}
      <path d={`M${L + 6.8} ${Y - 1.6} Q50 ${Y - 4} ${R - 6.8} ${Y - 1.6}`} stroke={isRainbowFrame ? RAINBOW[2] : frame} strokeWidth={w} fill="none" strokeLinecap="round" />
      <path d={lp} fill="none" stroke={ink} strokeWidth="0.35" opacity="0.6" />
      <path d={rp} fill="none" stroke={ink} strokeWidth="0.35" opacity="0.6" />
      <Deco extra={it.extra} />
    </g>
  );
};

// ---------------------------------------------------------------------------
// PINS — a small enamel pin on the chest. Drawn round (0,0), about 12 wide.
// ---------------------------------------------------------------------------
const PIN_ART = {
  book: (
    <g>
      <path d="M-5.5 -3.5 Q-3 -4.8 0 -3.2 Q3 -4.8 5.5 -3.5 L5.5 4 Q3 2.8 0 4.2 Q-3 2.8 -5.5 4 Z" fill="#fff" stroke={INK} strokeWidth="0.9" />
      <path d="M0 -3.2 L0 4.2" stroke={INK} strokeWidth="0.8" />
      <path d="M-5.5 -3.5 L-5.5 4 Q-3 2.8 0 4.2 L0 5.2 Q-3 3.8 -6.4 5 L-6.4 -2.8 Z" fill="#3a86ff" />
      <path d="M5.5 -3.5 L5.5 4 Q3 2.8 0 4.2 L0 5.2 Q3 3.8 6.4 5 L6.4 -2.8 Z" fill="#3a86ff" />
    </g>
  ),
  apple: (
    <g>
      <path d="M0 -2.4 C-2 -4.6 -6 -3.6 -5.6 0.6 C-5.2 4.4 -2.4 6.2 0 5 C2.4 6.2 5.2 4.4 5.6 0.6 C6 -3.6 2 -4.6 0 -2.4 Z" fill="#ef3b3b" stroke={INK} strokeWidth="0.9" />
      <path d="M0 -2.4 L0.6 -5.4" stroke="#6b3f22" strokeWidth="1" />
      <path d="M0.8 -4.4 Q3.6 -6.2 4.4 -3.6 Q2 -2.8 0.8 -4.4 Z" fill="#4cc35b" />
      <ellipse cx="-2.6" cy="-0.6" rx="1" ry="1.6" fill="#fff" opacity="0.6" />
    </g>
  ),
  star: <path d={starPath(0, 0.4, 6.2)} fill="#ffd23f" stroke={INK} strokeWidth="0.9" strokeLinejoin="round" />,
  heart: <path d="M0 5.4 C-6 1.6 -7 -2 -5.6 -4 C-4 -6 -1.4 -5.4 0 -3 C1.4 -5.4 4 -6 5.6 -4 C7 -2 6 1.6 0 5.4 Z" fill="#ff4d6d" stroke={INK} strokeWidth="0.9" />,
  smiley: (
    <g>
      <circle r="5.8" fill="#ffd23f" stroke={INK} strokeWidth="0.9" />
      <circle cx="-2" cy="-1.4" r="0.9" fill={INK} />
      <circle cx="2" cy="-1.4" r="0.9" fill={INK} />
      <path d="M-3 1.2 Q0 4.2 3 1.2" stroke={INK} strokeWidth="0.9" fill="none" strokeLinecap="round" />
    </g>
  ),
  musicnote: (
    <g>
      <path d="M-1 3.4 L-1 -5 L5 -6.4 L5 2" fill="none" stroke={INK} strokeWidth="1.8" />
      <ellipse cx="-3" cy="3.6" rx="2.6" ry="2" fill="#8b5cf6" stroke={INK} strokeWidth="0.8" />
      <ellipse cx="3" cy="2.2" rx="2.6" ry="2" fill="#8b5cf6" stroke={INK} strokeWidth="0.8" />
    </g>
  ),
  flower: (
    <g>
      {[0, 72, 144, 216, 288].map((a) => (
        <ellipse key={a} cx="0" cy="-3.4" rx="2.4" ry="3.2" fill="#ff8cc6" stroke={INK} strokeWidth="0.7" transform={`rotate(${a})`} />
      ))}
      <circle r="2" fill="#ffd23f" stroke={INK} strokeWidth="0.7" />
    </g>
  ),
  pawprint: (
    <g fill="#8a5a36" stroke={INK} strokeWidth="0.6">
      <ellipse cx="0" cy="2" rx="3.4" ry="2.8" />
      <circle cx="-4" cy="-1.4" r="1.5" />
      <circle cx="-1.6" cy="-4" r="1.5" />
      <circle cx="1.6" cy="-4" r="1.5" />
      <circle cx="4" cy="-1.4" r="1.5" />
    </g>
  ),
  sun: (
    <g>
      {Array.from({ length: 8 }, (_, i) => (
        <path key={i} d="M0 -6.4 L1.2 -4 L-1.2 -4 Z" fill="#ff9a3c" transform={`rotate(${i * 45})`} />
      ))}
      <circle r="3.8" fill="#ffd23f" stroke={INK} strokeWidth="0.8" />
    </g>
  ),
  lightning: <path d="M1.6 -6.4 L-4 1 L-0.4 1 L-1.8 6.4 L4 -1.2 L0.4 -1.2 Z" fill="#ffd23f" stroke={INK} strokeWidth="0.9" strokeLinejoin="round" />,
  butterfly: (
    <g>
      <path d="M0 0 C-2 -6 -7.4 -6.6 -6.6 -2 C-6 0.6 -3 0.8 0 0 Z" fill="#4fb8ff" stroke={INK} strokeWidth="0.8" />
      <path d="M0 0 C2 -6 7.4 -6.6 6.6 -2 C6 0.6 3 0.8 0 0 Z" fill="#4fb8ff" stroke={INK} strokeWidth="0.8" />
      <path d="M0 0.4 C-2 4.8 -6 5 -5.4 2 C-5 0.8 -2.4 0.6 0 0.4 Z" fill="#ff8cc6" stroke={INK} strokeWidth="0.8" />
      <path d="M0 0.4 C2 4.8 6 5 5.4 2 C5 0.8 2.4 0.6 0 0.4 Z" fill="#ff8cc6" stroke={INK} strokeWidth="0.8" />
      <circle cx="-4" cy="-3" r="0.9" fill="#fff" />
      <circle cx="4" cy="-3" r="0.9" fill="#fff" />
      <path d="M0 -3.4 L0 3.4" stroke={INK} strokeWidth="1.3" strokeLinecap="round" />
      <path d="M0 -3.4 Q-1 -5.6 -2.2 -6 M0 -3.4 Q1 -5.6 2.2 -6" stroke={INK} strokeWidth="0.6" fill="none" />
    </g>
  ),
  rainbow: (
    <g fill="none" strokeLinecap="round">
      {["#ff4d6d", "#ffd23f", "#4cd964", "#3a86ff"].map((c, i) => (
        <path key={c} d={`M${-6 + i * 1.4} 3 A${6 - i * 1.4} ${6 - i * 1.4} 0 0 1 ${6 - i * 1.4} 3`} stroke={c} strokeWidth="1.5" />
      ))}
      <ellipse cx="-5.4" cy="3.6" rx="2" ry="1.3" fill="#fff" stroke={INK} strokeWidth="0.5" />
      <ellipse cx="5.4" cy="3.6" rx="2" ry="1.3" fill="#fff" stroke={INK} strokeWidth="0.5" />
    </g>
  ),
  rocket: (
    <g transform="rotate(35)">
      <path d="M0 -6.6 C3 -4 3 1 2 3.4 L-2 3.4 C-3 1 -3 -4 0 -6.6 Z" fill="#f2f4f8" stroke={INK} strokeWidth="0.8" />
      <circle cy="-1.6" r="1.3" fill="#4fb8ff" stroke={INK} strokeWidth="0.5" />
      <path d="M-2 1 L-4 4.4 L-2 3.4 Z M2 1 L4 4.4 L2 3.4 Z" fill="#ff4d6d" stroke={INK} strokeWidth="0.5" />
      <path d="M-1.2 3.6 L0 6.4 L1.2 3.6 Z" fill="#ff9a3c" />
    </g>
  ),
  planet: (
    <g>
      <circle r="4.2" fill="#b07cff" stroke={INK} strokeWidth="0.8" />
      <ellipse rx="7" ry="2" fill="none" stroke="#ffd23f" strokeWidth="1.3" transform="rotate(-18)" />
      <path d="M-3 -1 q3 -2 6 0" stroke="#fff" strokeWidth="0.6" fill="none" opacity="0.7" />
    </g>
  ),
  dino: (
    <g>
      <path d="M-6 3.8 C-6 -1 -3 -2.6 0 -2.2 L2 -5.4 C4.4 -6.4 6.6 -5 6 -3 C5.6 -2 4.4 -1.8 3.6 -2 L3 3.8 L1.4 3.8 L1 1.6 L-2.6 1.6 L-3 3.8 Z" fill="#4cc35b" stroke={INK} strokeWidth="0.8" strokeLinejoin="round" />
      <circle cx="4.2" cy="-4" r="0.6" fill={INK} />
      <path d="M-4 -1 l1 -1.6 1 1.4 1 -1.6 1 1.4" stroke="#2f8a3e" strokeWidth="0.7" fill="none" />
    </g>
  ),
  ladybug: (
    <g>
      <circle cy="-4.2" r="2.2" fill={INK} />
      <ellipse rx="5" ry="5.4" cy="0.6" fill="#ef3b3b" stroke={INK} strokeWidth="0.8" />
      <path d="M0 -4.6 L0 6" stroke={INK} strokeWidth="0.8" />
      <circle cx="-2.4" cy="-0.6" r="1.1" fill={INK} />
      <circle cx="2.4" cy="-0.6" r="1.1" fill={INK} />
      <circle cx="-2.2" cy="3" r="0.9" fill={INK} />
      <circle cx="2.2" cy="3" r="0.9" fill={INK} />
    </g>
  ),
  crown: (
    <g>
      <path d="M-6 3.6 L-6.4 -3.6 L-3 -0.6 L0 -5.4 L3 -0.6 L6.4 -3.6 L6 3.6 Z" fill="#ffd23f" stroke={INK} strokeWidth="0.9" strokeLinejoin="round" />
      <circle cx="0" cy="1.4" r="1.2" fill="#ff4d6d" />
      <circle cx="-3.4" cy="1.8" r="0.8" fill="#3a86ff" />
      <circle cx="3.4" cy="1.8" r="0.8" fill="#3a86ff" />
    </g>
  ),
  gem: (
    <g>
      <path d="M-6 -1.6 L-3 -5 L3 -5 L6 -1.6 L0 6 Z" fill="#4fd1ff" stroke={INK} strokeWidth="0.9" strokeLinejoin="round" />
      <path d="M-6 -1.6 L6 -1.6 M-3 -5 L-1.6 -1.6 L0 6 L1.6 -1.6 L3 -5" stroke="#1f8fb8" strokeWidth="0.6" fill="none" />
      <path d="M-3.6 -2.6 L-2 -4.2" stroke="#fff" strokeWidth="0.9" strokeLinecap="round" />
    </g>
  ),
};

export const PinArt = ({ id }) => PIN_ART[String(id).replace("av-pin-", "")] || null;

export const Pin = ({ id, x = 66, y = 88, scale = 1.05 }) => {
  const art = PIN_ART[String(id).replace("av-pin-", "")];
  if (!art) return null;
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      <g transform="translate(0.6 0.8)" opacity="0.25">
        {art}
      </g>
      {art}
    </g>
  );
};
